import React, {useEffect, FormEvent, useState, useMemo} from 'react'
import * as AmplifyAuth from 'aws-amplify/auth'
import {Title} from '../../Atoms/Typography'
import {LabelInput} from '../../Atoms/Forms'
import {useForm} from 'react-hook-form'
import {InvisibleContainer, Row, Baseline} from '../../Layout/Grid'
import {FormSubmitButton, StyledLink} from '../../Atoms/Buttons'
import {useAppState} from '../../../state'
import {colors} from '../../../sharedComponents/colors'
import {LoginLogo, TitleWrapper} from './LoginComponents'
import {useTranslation} from 'react-i18next'
import {useCurrentRoute, useNavigation} from 'react-navi'
import {Helmet} from 'react-navi-helmet-async'
import authApi from '../../../state/auth'
import {NotificationType} from '../../../state/state'

interface Form {
  userName: string
  password: string
}
interface Creds {
  password?: string
  email?: string
  error?: string
}

type LoginStep = 'EMAIL' | 'PASSWORD'

const Login = (creds: Creds) => {
  const route = useCurrentRoute()
  const error = creds.error
  const fc = useForm<Form>({mode: 'onChange'})
  const {state, actions} = useAppState()
  const {t} = useTranslation('auth')
  const navi = useNavigation()

  const [step, setStep] = useState<LoginStep>('EMAIL')
  const [isResolving, setIsResolving] = useState(false)
  const [resolveError, setResolveError] = useState<string | null>(null)

  const {userName, password} = fc.watch()
  const email = useMemo(() => (userName ?? '').trim().toLowerCase(), [userName])

  const handlePasswordLogin = () => {
    actions.login({email, password})
  }
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setResolveError(null)

    if (step === 'PASSWORD') {
      handlePasswordLogin()
      return
    }

    if (!email) return

    setIsResolving(true)

    try {
      const result = (await authApi.resolveAuthenticationMethod(email)) as any
      // expected: { data: { method: 'PASSWORD' | 'SSO', provider?: string } }
      const method = result?.method
      const provider = result?.provider ?? ''
      if (method === 'SSO') {
        sessionStorage.setItem('ssoLoginEmail', email)
        await authApi.redirectToMicrosoftSso(provider)
        return
      }
      setStep('PASSWORD')
    } catch (err) {
      setResolveError(
        t('auth:resolveAuthenticationMethodFailed', 'Failed to resolve authentication method. Please try again later.')
      )
    } finally {
      setIsResolving(false)
    }
  }

  // use case: mobile app login transparently, using the component parameters (read from query string in router)
  useEffect(() => {
    if (!!creds.email && !!creds.password) {
      actions.login({email: creds.email, password: creds.password})
    }
  }, [creds.email, creds.password, actions])

  useEffect(() => {
    if (error === 'sso_email_mismatch') {
      actions.addNotification({
        id: 'sso_email_mismatch',
        type: NotificationType.ERROR,
        title: t('auth:sso.emailMismatchTitle', 'SSO Email Mismatch'),
        description: t(
          'auth:sso.emailMismatchMessage',
          'The email address associated with your SSO account does not match the email you entered. Please try logging in again with the same email you use for SSO.'
        ),
        visible: true,
        hideAfterDelay: 6000
      })
    }
  }, [error])

  return (
    <InvisibleContainer>
      <Helmet title={t('common:routes.loginSignup', 'Login / signup')} />
      <LoginLogo />
      <TitleWrapper>
        <Title level={2} color={colors.system.white}>
          {t('common:tagLine', 'Because you have better things to do')}
        </Title>
      </TitleWrapper>
      <form onSubmit={handleSubmit}>
        <Baseline>
          <LabelInput
            negative={true}
            autoComplete="username"
            type="email"
            labelText={t('auth:labels.emailAddress', 'Email address')}
            name="userName"
            id="userName"
            ref={fc.register({
              required: `${t('common:validation.emailRequired', 'Email address is mandatory')}`,
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/i,
                message: `${t('common:validation.emailInvalid', 'Invalid email address')}`
              }
            })}
            errorMsg={!!fc.errors.userName ? fc.errors.userName!.message : undefined}
          />
          {step === 'PASSWORD' && (
            <LabelInput
              negative={true}
              labelText={t('auth:labels.password', 'Password')}
              name="password"
              id="password"
              type="password"
              ref={fc.register({required: true})}
              errorMsg={
                state.authenticationError
                  ? t(`auth:authenticationError.${state.authenticationError}`, state.authenticationError)
                  : undefined
              }
            />
          )}
          {resolveError && <div style={{marginTop: 8, color: 'white'}}>{resolveError}</div>}
          <Row>
            {step === 'PASSWORD' && (
              <StyledLink
                negative="negative"
                onClick={() => {
                  navi.navigate('/login/forgot')
                }}
              >
                {t('auth:labels.forgotPassword', 'Forgot password?')}
              </StyledLink>
            )}
          </Row>
          {error && (
            <div style={{marginTop: 8, color: 'red'}}>
              {t(
                'auth:sso.emailMismatchMessage',
                'The email address associated with your SSO account does not match the email you entered. Please try logging in again with the same email you use for SSO.'
              )}
            </div>
          )}
          <FormSubmitButton
            type="submit"
            disabled={
              state.isAuthenticating || isResolving || !fc.formState.isValid || (step === 'PASSWORD' && !password)
            }
          >
            {isResolving
              ? t('auth:labels.checking', 'Checking...')
              : step === 'PASSWORD'
              ? state.isAuthenticating
                ? t('auth:labels.authenticating', 'Authenticating')
                : t('auth:labels.login', 'Log in')
              : t('auth:labels.continue', 'Continue')}
          </FormSubmitButton>
        </Baseline>
      </form>
    </InvisibleContainer>
  )
}

export default Login
